import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const PURITY_FACTOR = { '24K': 24 / 22, '22K': 1, '18K': 18 / 22, '14K': 14 / 22 };

const formatINR = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const GainBadge = ({ pct, abs }) => (
    <div style={{ color: pct >= 0 ? '#1a7a4a' : '#c0392b', fontWeight: '700', fontSize: '0.9rem' }}>
        {pct >= 0 ? '▲' : '▼'} {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
        <div style={{ fontSize: '0.78rem', fontWeight: '400' }}>{abs >= 0 ? '+' : ''}{formatINR(abs)}</div>
    </div>
);

const SummaryCard = ({ label, value, subValue, color, highlight }) => (
    <div style={{
        background: highlight ? `linear-gradient(135deg, ${color}18, ${color}05)` : '#fff',
        border: highlight ? `2px solid ${color}30` : '1px solid #f0f0f0',
        borderRadius: '14px', padding: '20px', textAlign: 'center',
        boxShadow: highlight ? `0 4px 20px ${color}15` : '0 2px 12px rgba(0,0,0,0.05)',
    }}>
        <div style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontWeight: '600' }}>{label}</div>
        <div style={{ fontSize: '1.25rem', fontWeight: '700', color, lineHeight: 1.2 }}>{value}</div>
        {subValue && <div style={{ fontSize: '0.85rem', fontWeight: '600', color, marginTop: '4px' }}>{subValue}</div>}
    </div>
);

// ─── Manual Calculator ─────────────────────────────────────────────────────────
const ManualCalculator = ({ rates }) => {
    const emptyRow = { name: '', metalType: 'Gold', purity: '22K', weight: '', purchaseRate: '' };
    const [rows, setRows] = useState([{ ...emptyRow }]);
    const [results, setResults] = useState(null);

    const currentRateFor = (metalType, purity) => {
        if (!rates) return 0;
        if (metalType === 'Silver') return rates['Silver'] || 0;
        const base = rates['Gold 22K'] || rates['Gold 24K'] || 0;
        return base * (PURITY_FACTOR[purity] || 1);
    };

    const updateRow = (i, field, val) => {
        const next = rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r);
        setRows(next);
        setResults(null);
    };

    const addRow = () => setRows([...rows, { ...emptyRow }]);
    const removeRow = (i) => { if (rows.length > 1) { setRows(rows.filter((_, idx) => idx !== i)); setResults(null); } };

    const calculate = () => {
        const enriched = rows.map(r => {
            const weight = parseFloat(r.weight) || 0;
            const purchaseRate = parseFloat(r.purchaseRate) || 0;
            const curRate = currentRateFor(r.metalType, r.purity);
            const purchaseValue = weight * purchaseRate;
            const currentValue = weight * curRate;
            const gain = currentValue - purchaseValue;
            const gainPct = purchaseValue > 0 ? (gain / purchaseValue) * 100 : 0;
            return { ...r, weight, purchaseRate, curRate, purchaseValue, currentValue, gain, gainPct };
        }).filter(r => r.weight > 0 && r.purchaseRate > 0);
        setResults(enriched);
    };

    const totalPurchase = results ? results.reduce((s, r) => s + r.purchaseValue, 0) : 0;
    const totalCurrent = results ? results.reduce((s, r) => s + r.currentValue, 0) : 0;
    const totalGain = totalCurrent - totalPurchase;
    const totalGainPct = totalPurchase > 0 ? (totalGain / totalPurchase) * 100 : 0;

    const inputStyle = {
        width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: '8px',
        fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
    };
    const selectStyle = { ...inputStyle, background: '#fff', cursor: 'pointer' };

    return (
        <div>
            {rates && (
                <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '10px', padding: '12px 16px', marginBottom: '24px', fontSize: '0.88rem', color: '#795548' }}>
                    <strong>Live rates loaded:</strong>&nbsp;
                    {rates['Gold 22K'] && `Gold 22K — ${formatINR(rates['Gold 22K'])}/g`}
                    {rates['Gold 24K'] && ` · Gold 24K — ${formatINR(rates['Gold 24K'])}/g`}
                    {rates['Silver'] && ` · Silver — ${formatINR(rates['Silver'])}/g`}
                </div>
            )}
            {!rates && (
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '10px', padding: '12px 16px', marginBottom: '24px', fontSize: '0.88rem', color: '#856404' }}>
                    Live rates are loading… Current value column will populate once loaded.
                </div>
            )}

            {/* Item rows */}
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                    <thead>
                        <tr style={{ background: '#fafaf8' }}>
                            {['Item Name (optional)', 'Metal', 'Purity', 'Weight (g)', 'Your Rate (₹/g)', ''].map(h => (
                                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '0.76rem', color: '#888', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f8f8f8' }}>
                                <td style={{ padding: '8px 12px' }}>
                                    <input style={inputStyle} placeholder="e.g. Gold Necklace" value={row.name} onChange={e => updateRow(i, 'name', e.target.value)} />
                                </td>
                                <td style={{ padding: '8px 12px' }}>
                                    <select style={selectStyle} value={row.metalType} onChange={e => updateRow(i, 'metalType', e.target.value)}>
                                        <option>Gold</option>
                                        <option>Silver</option>
                                    </select>
                                </td>
                                <td style={{ padding: '8px 12px' }}>
                                    <select style={selectStyle} value={row.purity} onChange={e => updateRow(i, 'purity', e.target.value)} disabled={row.metalType === 'Silver'}>
                                        {row.metalType === 'Silver' ? <option>999</option> : ['24K', '22K', '18K', '14K'].map(p => <option key={p}>{p}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: '8px 12px' }}>
                                    <input style={inputStyle} type="number" min="0" step="0.1" placeholder="e.g. 10.5" value={row.weight} onChange={e => updateRow(i, 'weight', e.target.value)} />
                                </td>
                                <td style={{ padding: '8px 12px' }}>
                                    <input style={inputStyle} type="number" min="0" step="1" placeholder="e.g. 6500" value={row.purchaseRate} onChange={e => updateRow(i, 'purchaseRate', e.target.value)} />
                                </td>
                                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                    <button onClick={() => removeRow(i)} style={{ background: 'none', border: 'none', color: '#ccc', fontSize: '1.2rem', cursor: 'pointer', padding: '4px 8px' }} title="Remove">×</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
                <button onClick={addRow} style={{ background: '#fff', border: '1.5px dashed #B8860B', color: '#B8860B', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', fontFamily: 'inherit' }}>
                    + Add Another Item
                </button>
                <button onClick={calculate} style={{ background: 'linear-gradient(135deg, #B8860B, #8B6914)', color: '#fff', border: 'none', padding: '10px 28px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.95rem', fontFamily: 'inherit' }}>
                    Calculate Now →
                </button>
            </div>

            {/* Results */}
            {results && results.length > 0 && (
                <div style={{ marginTop: '32px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px', marginBottom: '24px' }}>
                        <SummaryCard label="Total Gold Weight" value={`${results.filter(r => r.metalType === 'Gold').reduce((s, r) => s + r.weight, 0).toFixed(2)}g`} color="#B8860B" />
                        <SummaryCard label="Amount Paid" value={formatINR(totalPurchase)} color="#555" />
                        <SummaryCard label="Current Value" value={formatINR(totalCurrent)} color={totalGain >= 0 ? '#1a7a4a' : '#c0392b'} highlight />
                        <SummaryCard label="Total Return" value={`${totalGain >= 0 ? '+' : ''}${formatINR(totalGain)}`} subValue={`${totalGainPct >= 0 ? '+' : ''}${totalGainPct.toFixed(1)}%`} color={totalGain >= 0 ? '#1a7a4a' : '#c0392b'} highlight />
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                            <thead>
                                <tr style={{ background: '#fafaf8' }}>
                                    {['Item', 'Metal', 'Weight', 'Rate Paid', 'Today\'s Rate', 'Paid', 'Now Worth', 'Return'].map(h => (
                                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.76rem', color: '#888', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f8f8f8' }}>
                                        <td style={{ padding: '12px 14px', fontWeight: '600', color: '#1a1a1a', fontSize: '0.9rem' }}>{r.name || `Item ${i + 1}`}</td>
                                        <td style={{ padding: '12px 14px' }}>
                                            <span style={{ background: r.metalType === 'Gold' ? '#FFF8E1' : '#F5F5F5', color: r.metalType === 'Gold' ? '#B8860B' : '#666', padding: '3px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '600' }}>
                                                {r.metalType} {r.metalType !== 'Silver' ? r.purity : ''}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 14px', color: '#333' }}>{r.weight}g</td>
                                        <td style={{ padding: '12px 14px', color: '#555', fontSize: '0.85rem' }}>{formatINR(r.purchaseRate)}/g</td>
                                        <td style={{ padding: '12px 14px', color: '#555', fontSize: '0.85rem', fontWeight: '500' }}>{r.curRate > 0 ? `${formatINR(r.curRate)}/g` : '—'}</td>
                                        <td style={{ padding: '12px 14px', color: '#333' }}>{formatINR(r.purchaseValue)}</td>
                                        <td style={{ padding: '12px 14px', color: r.gain >= 0 ? '#1a7a4a' : '#c0392b', fontWeight: '600' }}>{r.curRate > 0 ? formatINR(r.currentValue) : '—'}</td>
                                        <td style={{ padding: '12px 14px' }}>{r.curRate > 0 ? <GainBadge pct={r.gainPct} abs={r.gain} /> : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                            {results.length > 1 && (
                                <tfoot>
                                    <tr style={{ background: '#fafaf8', borderTop: '2px solid #e8e8e8' }}>
                                        <td colSpan={5} style={{ padding: '12px 14px', fontWeight: '700', color: '#1a1a1a' }}>Total</td>
                                        <td style={{ padding: '12px 14px', fontWeight: '700' }}>{formatINR(totalPurchase)}</td>
                                        <td style={{ padding: '12px 14px', fontWeight: '700', color: totalGain >= 0 ? '#1a7a4a' : '#c0392b' }}>{formatINR(totalCurrent)}</td>
                                        <td style={{ padding: '12px 14px' }}><GainBadge pct={totalGainPct} abs={totalGain} /></td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            )}
            {results && results.length === 0 && (
                <div style={{ textAlign: 'center', padding: '30px', color: '#888', marginTop: '20px' }}>
                    Please fill in weight and purchase rate for at least one item.
                </div>
            )}

            <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '10px', padding: '14px 18px', marginTop: '24px', fontSize: '0.82rem', color: '#795548' }}>
                <strong>Note:</strong> Current value shown is the <em>metal value only</em>, based on live MAB Jewels rates.
                Actual resale value includes assessment of making charges. For an exchange quote, <Link to="/contact" style={{ color: '#B8860B' }}>contact us</Link>.
            </div>
        </div>
    );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const GoldInvestmentTrackerPage = () => {
    const [activeTab, setActiveTab] = useState('calculator');
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [portfolio, setPortfolio] = useState(null);
    const [rates, setRates] = useState(null);
    const [error, setError] = useState('');
    const [searched, setSearched] = useState(false);

    // Fetch live rates on mount (used by both tabs)
    useEffect(() => {
        axios.get('/api/rates').then(res => {
            const map = {};
            res.data.forEach(r => { map[r.metalType] = r.rate; });
            setRates(map);
        }).catch(() => { });
    }, []);

    const handleSearch = async (e) => {
        e.preventDefault();
        const digits = phone.replace(/\D/g, '');
        if (digits.length < 10) { setError('Please enter a valid 10-digit mobile number.'); return; }
        setLoading(true); setError(''); setSearched(true); setPortfolio(null);
        try {
            const res = await axios.get(`/api/bills/by-phone/${digits}`);
            setPortfolio(res.data);
        } catch {
            setError('Could not fetch portfolio. Please verify your mobile number and try again.');
        } finally { setLoading(false); }
    };

    const currentRateFor = (metalType, purity) => {
        if (!rates) return 0;
        if (metalType === 'Silver') return rates['Silver'] || 0;
        const base22K = rates['Gold 22K'] || rates['Gold 24K'] || 0;
        return base22K * (PURITY_FACTOR[purity] || 1);
    };

    const enrichedItems = portfolio?.flatMap(bill =>
        bill.items.map(item => {
            const curRate = currentRateFor(item.metalType, item.purity);
            const purchaseValue = item.netWeight * item.rateAtPurchase;
            const currentValue = item.netWeight * curRate;
            const gain = currentValue - purchaseValue;
            const gainPct = purchaseValue > 0 ? (gain / purchaseValue) * 100 : 0;
            return { ...item, billNumber: bill.billNumber, purchaseDate: bill.purchaseDate, curRate, purchaseValue, currentValue, gain, gainPct };
        })
    ) || [];

    const totalPurchaseValue = enrichedItems.reduce((s, i) => s + i.purchaseValue, 0);
    const totalCurrentValue = enrichedItems.reduce((s, i) => s + i.currentValue, 0);
    const totalGain = totalCurrentValue - totalPurchaseValue;
    const totalGainPct = totalPurchaseValue > 0 ? (totalGain / totalPurchaseValue) * 100 : 0;
    const totalGoldGrams = enrichedItems.filter(i => i.metalType === 'Gold').reduce((s, i) => s + i.netWeight, 0);

    const tabStyle = (active) => ({
        padding: '12px 28px', border: 'none', borderRadius: '8px', fontFamily: 'inherit',
        fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
        background: active ? '#B8860B' : '#f5f5f5',
        color: active ? '#fff' : '#666',
    });

    return (
        <div style={{ minHeight: '100vh', background: '#fafaf8' }}>
            {/* Hero */}
            <div style={{
                background: 'linear-gradient(135deg, #1a1209 0%, #3d2b0e 50%, #5c3d11 100%)',
                padding: '60px 20px 50px', textAlign: 'center', position: 'relative', overflow: 'hidden',
            }}>
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(184,134,11,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(212,175,55,0.1) 0%, transparent 40%)' }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ fontSize: '2.8rem', marginBottom: '14px' }}>📈</div>
                    <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.8rem, 5vw, 2.8rem)', color: '#F5D88C', fontWeight: '700', marginBottom: '10px', lineHeight: 1.2 }}>
                        Your Gold Investment Portfolio
                    </h1>
                    <p style={{ color: 'rgba(245,216,140,0.8)', fontSize: '1rem', maxWidth: '560px', margin: '0 auto 24px', lineHeight: 1.6 }}>
                        See what your jewellery is worth today vs what you paid — using live gold rates.
                    </p>
                    {rates && (
                        <div style={{ display: 'inline-flex', gap: '20px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(245,216,140,0.3)', borderRadius: '10px', padding: '10px 20px', flexWrap: 'wrap', justifyContent: 'center' }}>
                            {rates['Gold 22K'] && <span style={{ color: '#F5D88C', fontSize: '0.85rem', fontWeight: '600' }}>Gold 22K: {formatINR(rates['Gold 22K'])}/g</span>}
                            {rates['Gold 24K'] && <span style={{ color: '#F5D88C', fontSize: '0.85rem', fontWeight: '600' }}>Gold 24K: {formatINR(rates['Gold 24K'])}/g</span>}
                            {rates['Silver'] && <span style={{ color: '#C0C0C0', fontSize: '0.85rem', fontWeight: '600' }}>Silver: {formatINR(rates['Silver'])}/g</span>}
                        </div>
                    )}
                </div>
            </div>

            <div style={{ maxWidth: '960px', margin: '0 auto', padding: '36px 20px 80px' }}>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '32px', background: '#f0f0f0', padding: '6px', borderRadius: '12px', width: 'fit-content' }}>
                    <button style={tabStyle(activeTab === 'calculator')} onClick={() => setActiveTab('calculator')}>
                        🧮 Calculate Manually
                    </button>
                    <button style={tabStyle(activeTab === 'portfolio')} onClick={() => setActiveTab('portfolio')}>
                        📋 My Purchase History
                    </button>
                </div>

                {/* ── Calculator Tab ── */}
                {activeTab === 'calculator' && (
                    <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
                        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', color: '#1a1a1a', marginBottom: '6px' }}>
                            Gold Value Calculator
                        </h2>
                        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '24px' }}>
                            Enter the weight and rate you paid — see what it's worth at today's live rate.
                        </p>
                        <ManualCalculator rates={rates} />
                    </div>
                )}

                {/* ── Portfolio Tab ── */}
                {activeTab === 'portfolio' && (
                    <div>
                        {/* Search Card */}
                        <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', marginBottom: '32px' }}>
                            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', color: '#1a1a1a', marginBottom: '6px' }}>
                                Look Up Your Purchases
                            </h2>
                            <p style={{ color: '#666', marginBottom: '22px', fontSize: '0.9rem' }}>
                                Enter the mobile number you used at MAB Jewels to view your full purchase history automatically.
                            </p>
                            <form onSubmit={handleSearch} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <input
                                    type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                                    placeholder="Enter your 10-digit mobile number" maxLength={10}
                                    style={{ flex: 1, minWidth: '240px', padding: '14px 18px', border: '2px solid #e0e0e0', borderRadius: '10px', fontSize: '1rem', outline: 'none', fontFamily: 'inherit' }}
                                    onFocus={e => e.target.style.borderColor = '#B8860B'}
                                    onBlur={e => e.target.style.borderColor = '#e0e0e0'}
                                />
                                <button type="submit" disabled={loading} style={{ background: loading ? '#ccc' : 'linear-gradient(135deg, #B8860B, #8B6914)', color: '#fff', border: 'none', borderRadius: '10px', padding: '14px 28px', fontSize: '1rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                    {loading ? 'Checking...' : 'View Portfolio →'}
                                </button>
                            </form>
                            {error && <p style={{ color: '#c0392b', marginTop: '12px', fontSize: '0.9rem' }}>{error}</p>}
                            <p style={{ color: '#999', fontSize: '0.78rem', marginTop: '14px' }}>Your data is private. We only show purchases linked to this number.</p>
                        </div>

                        {/* Results */}
                        {searched && !loading && portfolio !== null && (
                            <>
                                {enrichedItems.length === 0 ? (
                                    <div style={{ background: '#fff', borderRadius: '16px', padding: '50px 32px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', textAlign: 'center' }}>
                                        <div style={{ fontSize: '2.5rem', marginBottom: '14px' }}>🔍</div>
                                        <h3 style={{ fontFamily: 'Playfair Display, serif', color: '#1a1a1a', marginBottom: '8px' }}>No purchases found</h3>
                                        <p style={{ color: '#666', marginBottom: '20px' }}>No purchases found for this number.<br />Try the manual calculator above, or contact us.</p>
                                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <button onClick={() => setActiveTab('calculator')} style={{ background: '#B8860B', color: '#fff', padding: '12px 22px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>
                                                Use Calculator Instead
                                            </button>
                                            <Link to="/contact" style={{ background: '#f5f5f5', color: '#333', padding: '12px 22px', borderRadius: '8px', textDecoration: 'none', fontWeight: '600' }}>Contact Us</Link>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginBottom: '28px' }}>
                                            <SummaryCard label="Total Items" value={enrichedItems.length} color="#B8860B" />
                                            <SummaryCard label="Gold Owned" value={`${totalGoldGrams.toFixed(2)}g`} color="#B8860B" />
                                            <SummaryCard label="Amount Paid" value={formatINR(totalPurchaseValue)} color="#555" />
                                            <SummaryCard label="Current Value" value={formatINR(totalCurrentValue)} color={totalGain >= 0 ? '#1a7a4a' : '#c0392b'} highlight />
                                            <SummaryCard label="Total Return" value={`${totalGain >= 0 ? '+' : ''}${formatINR(totalGain)}`} subValue={`${totalGainPct >= 0 ? '+' : ''}${totalGainPct.toFixed(1)}%`} color={totalGain >= 0 ? '#1a7a4a' : '#c0392b'} highlight />
                                        </div>

                                        <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                                            <div style={{ padding: '22px 26px 14px', borderBottom: '1px solid #f0f0f0' }}>
                                                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', color: '#1a1a1a' }}>
                                                    Portfolio — {portfolio[0]?.customerName}
                                                </h3>
                                                <p style={{ color: '#888', fontSize: '0.82rem', marginTop: '4px' }}>Metal value only, based on current rates. Does not include making charges.</p>
                                            </div>
                                            <div style={{ overflowX: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ background: '#fafaf8' }}>
                                                            {['Item', 'Date', 'Metal', 'Weight', 'Rate Paid', 'Today Rate', 'Paid', 'Now Worth', 'Return'].map(h => (
                                                                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.74rem', color: '#888', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {enrichedItems.map((item, idx) => (
                                                            <tr key={idx} style={{ borderBottom: '1px solid #f8f8f8' }}
                                                                onMouseEnter={e => e.currentTarget.style.background = '#fffdf5'}
                                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                                <td style={{ padding: '12px 14px', fontWeight: '600', color: '#1a1a1a', fontSize: '0.88rem' }}>
                                                                    <div>{item.name}</div>
                                                                    <div style={{ fontSize: '0.72rem', color: '#bbb', fontWeight: '400' }}>{item.billNumber}</div>
                                                                </td>
                                                                <td style={{ padding: '12px 14px', color: '#666', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{formatDate(item.purchaseDate)}</td>
                                                                <td style={{ padding: '12px 14px' }}>
                                                                    <span style={{ background: item.metalType === 'Gold' ? '#FFF8E1' : '#F5F5F5', color: item.metalType === 'Gold' ? '#B8860B' : '#666', padding: '3px 10px', borderRadius: '20px', fontSize: '0.76rem', fontWeight: '600' }}>
                                                                        {item.metalType} {item.purity}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '12px 14px', color: '#333', fontSize: '0.88rem' }}>{item.netWeight}g</td>
                                                                <td style={{ padding: '12px 14px', color: '#555', fontSize: '0.82rem' }}>{formatINR(item.rateAtPurchase)}/g</td>
                                                                <td style={{ padding: '12px 14px', color: '#555', fontSize: '0.82rem', fontWeight: '500' }}>{formatINR(item.curRate)}/g</td>
                                                                <td style={{ padding: '12px 14px', color: '#333', fontSize: '0.88rem' }}>{formatINR(item.purchaseValue)}</td>
                                                                <td style={{ padding: '12px 14px', color: item.gain >= 0 ? '#1a7a4a' : '#c0392b', fontSize: '0.88rem', fontWeight: '600' }}>{formatINR(item.currentValue)}</td>
                                                                <td style={{ padding: '12px 14px' }}><GainBadge pct={item.gainPct} abs={item.gain} /></td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr style={{ background: '#fafaf8', borderTop: '2px solid #e8e8e8' }}>
                                                            <td colSpan={6} style={{ padding: '12px 14px', fontWeight: '700', color: '#1a1a1a' }}>Total Portfolio</td>
                                                            <td style={{ padding: '12px 14px', fontWeight: '700' }}>{formatINR(totalPurchaseValue)}</td>
                                                            <td style={{ padding: '12px 14px', fontWeight: '700', color: totalGain >= 0 ? '#1a7a4a' : '#c0392b' }}>{formatINR(totalCurrentValue)}</td>
                                                            <td style={{ padding: '12px 14px' }}><GainBadge pct={totalGainPct} abs={totalGain} /></td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>

                                        <div style={{ textAlign: 'center', marginTop: '28px' }}>
                                            <a href="https://wa.me/91XXXXXXXXXX?text=Hi MAB Jewels! I checked my gold portfolio and would like to know about exchange/upgrade options."
                                                target="_blank" rel="noopener noreferrer"
                                                style={{ background: '#25D366', color: '#fff', padding: '13px 26px', borderRadius: '10px', textDecoration: 'none', fontWeight: '600', fontSize: '0.95rem', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                                                Chat on WhatsApp for Exchange / Upgrade
                                            </a>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {!searched && (
                            <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 16px rgba(0,0,0,0.05)', textAlign: 'center', color: '#888' }}>
                                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🔒</div>
                                <p style={{ fontSize: '0.9rem' }}>Your purchase history is private and tied to your registered mobile number.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GoldInvestmentTrackerPage;
